import { useBuilderStore } from './store';
import type { FormConsentMode } from '@engage/types';
import { Btn, Icon, ICONS, INPUT_STYLE, Row, Section } from '../ui/atoms';
import { FORM_UI_COLORS as C, FORM_UI_FONT } from '../ui/tokens';
import { ImageUploadField } from '../../../components/ui/ImageUploadField';

const BRAND_KEYS = [
  { key: 'primary_color', label: 'Primary' },
  { key: 'accent_color', label: 'Accent' },
  { key: 'background_color', label: 'Background' },
  { key: 'text_color', label: 'Text' },
] as const;

export function FormSettings() {
  const s = useBuilderStore();
  const isContact = s.pathType === 'CONTACT';

  return (
    <div style={{ fontFamily: FORM_UI_FONT }}>
      <Section title="GENERAL">
        <Row label="NAME">
          <input
            value={s.name}
            onChange={(e) => s.setName(e.target.value)}
            style={INPUT_STYLE}
          />
        </Row>
        <Row
          label="SLUG / PATH"
          hint={
            isContact
              ? 'Public hosted page lives at /f/<slug>. Used in embeds and email links.'
              : 'Candidate path serves at /c/<token>. Tokens are per-candidate, per-send.'
          }
        >
          <div
            style={{
              ...INPUT_STYLE,
              fontFamily: 'ui-monospace, monospace',
              fontSize: 12,
              color: C.faint,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            forms.engage.jobtetris.com/
            {isContact ? (
              <input
                value={s.slug}
                onChange={(e) => s.setSlug(e.target.value)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: C.indigo,
                  fontFamily: 'ui-monospace, monospace',
                  fontSize: 12,
                  fontWeight: 600,
                  outline: 'none',
                  flex: 1,
                }}
              />
            ) : (
              <span style={{ color: C.indigo }}>c/•••••</span>
            )}
          </div>
        </Row>
      </Section>

      <Section
        title="BRANDING"
        note="Per-form sparse override on top of the org BrandKit. Written as CSS custom properties on the form root. This is client branding, distinct from the Montserrat/indigo product UI."
      >
        {BRAND_KEYS.map(({ key, label }) => (
          <BrandColorRow key={key} label={label} k={key} />
        ))}
        <Row label="LOGO">
          <ImageUploadField
            value={s.brandOverride?.logo_url}
            onChange={(url) =>
              s.setBrandOverride({
                ...(s.brandOverride ?? {}),
                logo_url: url,
              })
            }
            inputStyle={INPUT_STYLE}
            testId="form-logo"
          />
        </Row>
        <Row
          label="FONT"
          hint="Web-safe list or a custom web font (name + URL) supplied by the client."
        >
          <div
            style={{
              ...INPUT_STYLE,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
            }}
          >
            Montserrat (default)
            <Icon d={ICONS.chev} size={14} color={C.faint} sw={2} />
          </div>
        </Row>
        <button
          type="button"
          onClick={() => s.setBrandOverride(null)}
          disabled={!s.brandOverride}
          style={{
            fontSize: 11.5,
            color: C.indigoSoft,
            fontWeight: 600,
            cursor: s.brandOverride ? 'pointer' : 'not-allowed',
            background: C.bg,
            border: `1px solid ${C.line}`,
            borderRadius: 8,
            padding: '8px 12px',
            width: '100%',
            opacity: s.brandOverride ? 1 : 0.6,
            fontFamily: FORM_UI_FONT,
          }}
        >
          Reset all overrides to BrandKit
        </button>
      </Section>

      <Section title="CONSENT" note="HubSpot-style block at the bottom of the form.">
        <ConsentModeSelector
          value={s.consentMode ?? 'DISABLED'}
          onChange={(mode) => s.setConsent(mode, s.consentText)}
        />
        {s.consentMode === 'CHECKBOX' && (
          <div
            style={{
              fontSize: 10.8,
              color: C.warn,
              background: C.warnBg,
              borderRadius: 8,
              padding: '8px 10px',
              lineHeight: 1.45,
              marginBottom: 10,
            }}
          >
            ⚠ Checkbox mode is GDPR-strict opt-in. It typically loses top-of-funnel: fewer
            completions but cleaner consent.
          </div>
        )}
        {s.consentMode && s.consentMode !== 'DISABLED' && (
          <textarea
            value={s.consentText ?? ''}
            onChange={(e) => s.setConsent(s.consentMode, e.target.value || null)}
            placeholder="By submitting, you agree we may contact you about relevant roles."
            rows={3}
            style={{
              ...INPUT_STYLE,
              minHeight: 64,
              resize: 'vertical',
              fontFamily: FORM_UI_FONT,
              lineHeight: 1.5,
            }}
          />
        )}
      </Section>

      <Section
        title="GATED CONTENT"
        note={
          isContact
            ? 'Attach a document recipients receive after submitting. Auto-download releases immediately; Email sends via Postmark.'
            : 'Contact-path only: not available on candidate forms.'
        }
      >
        <div
          style={{
            opacity: isContact ? 1 : 0.4,
            pointerEvents: isContact ? 'auto' : 'none',
          }}
        >
          <Row label="DOCUMENT">
            <input
              value={s.gatedDocumentId ?? ''}
              onChange={(e) => s.setGated({ document_id: e.target.value || null })}
              placeholder="Upload a document or paste a document ID"
              style={INPUT_STYLE}
            />
          </Row>
          <Row label="DELIVERY">
            <div style={{ display: 'flex', gap: 6 }}>
              {(['AUTO_DOWNLOAD', 'EMAIL'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => s.setGated({ delivery_mode: mode })}
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    padding: '8px 0',
                    borderRadius: 8,
                    fontSize: 11.5,
                    fontWeight: 600,
                    border: `1px solid ${s.gatedDeliveryMode === mode ? C.indigo : C.line}`,
                    background: s.gatedDeliveryMode === mode ? C.indigo : '#fff',
                    color: s.gatedDeliveryMode === mode ? '#fff' : C.sub,
                    cursor: 'pointer',
                    fontFamily: FORM_UI_FONT,
                  }}
                >
                  {mode === 'AUTO_DOWNLOAD' ? 'Auto-download' : 'Email'}
                </button>
              ))}
            </div>
          </Row>
          <Row label="TTL">
            <TtlSelect
              value={s.gatedDocumentTtlSeconds}
              onChange={(v) => s.setGated({ ttl_seconds: v })}
            />
          </Row>
          {s.gatedDeliveryMode === 'EMAIL' && (
            <Row
              label="EMAIL SUBJECT"
              hint="Supports two merge tokens: {{document_name}} and {{org_name}}."
            >
              <input
                value={s.gatedEmailSubject ?? ''}
                onChange={(e) => s.setGated({ email_subject: e.target.value || null })}
                placeholder="Your {{document_name}} from {{org_name}}"
                style={INPUT_STYLE}
              />
            </Row>
          )}
        </div>
      </Section>
    </div>
  );
}

function ConsentModeSelector(props: {
  value: FormConsentMode;
  onChange: (v: FormConsentMode) => void;
}) {
  const options: FormConsentMode[] = ['DISABLED', 'NOTICE', 'CHECKBOX'];
  const labels: Record<FormConsentMode, string> = {
    DISABLED: 'Disabled',
    NOTICE: 'Notice',
    CHECKBOX: 'Checkbox',
  };
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
      {options.map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => props.onChange(m)}
          style={{
            flex: 1,
            fontFamily: FORM_UI_FONT,
            fontSize: 11.5,
            fontWeight: 600,
            cursor: 'pointer',
            padding: '8px 0',
            borderRadius: 8,
            border: `1px solid ${props.value === m ? C.indigo : C.line}`,
            background: props.value === m ? C.indigo : '#fff',
            color: props.value === m ? '#fff' : C.sub,
          }}
        >
          {labels[m]}
        </button>
      ))}
    </div>
  );
}

function TtlSelect(props: {
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  const presets = [
    { label: '1 day', value: 24 * 60 * 60 },
    { label: '7 days', value: 7 * 24 * 60 * 60 },
    { label: '14 days', value: 14 * 24 * 60 * 60 },
    { label: '30 days', value: 30 * 24 * 60 * 60 },
  ];
  const matched = presets.find((p) => p.value === props.value);
  const ctrlValue = props.value === null ? 'inherit' : matched ? String(props.value) : 'custom';
  return (
    <select
      value={ctrlValue}
      onChange={(e) => {
        const v = e.target.value;
        if (v === 'inherit') props.onChange(null);
        else if (v === 'custom') props.onChange(props.value ?? 7 * 24 * 60 * 60);
        else props.onChange(Number(v));
      }}
      style={INPUT_STYLE}
    >
      <option value="inherit">Inherit from org default</option>
      {presets.map((p) => (
        <option key={p.value} value={p.value}>
          {p.label}
        </option>
      ))}
      <option value="custom">Custom…</option>
    </select>
  );
}

function BrandColorRow(props: {
  label: string;
  k: 'primary_color' | 'accent_color' | 'background_color' | 'text_color';
}) {
  const s = useBuilderStore();
  const current = s.brandOverride?.[props.k] ?? '';
  const reset = () => {
    const next = { ...(s.brandOverride ?? {}) } as Record<string, unknown>;
    delete next[props.k];
    s.setBrandOverride(Object.keys(next).length > 0 ? next : null);
  };
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 0',
        borderBottom: `1px solid ${C.line}`,
      }}
    >
      <span style={{ fontSize: 12.5, color: C.ink, fontWeight: 500 }}>{props.label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <label
          style={{
            width: 22,
            height: 22,
            borderRadius: 6,
            background: current || '#FFFFFF',
            border: `1px solid ${C.line}`,
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <input
            type="color"
            value={current || '#000000'}
            onChange={(e) =>
              s.setBrandOverride({ ...(s.brandOverride ?? {}), [props.k]: e.target.value })
            }
            style={{
              opacity: 0,
              position: 'absolute',
              inset: 0,
              cursor: 'pointer',
              width: '100%',
              height: '100%',
            }}
          />
        </label>
        <span
          style={{
            fontFamily: 'ui-monospace, monospace',
            fontSize: 11.5,
            color: current ? C.sub : C.faint,
          }}
        >
          {current || 'inherit'}
        </span>
        {current && (
          <button
            type="button"
            onClick={reset}
            style={{
              fontSize: 10.5,
              color: C.tealDark,
              fontWeight: 600,
              cursor: 'pointer',
              background: 'transparent',
              border: 'none',
              fontFamily: FORM_UI_FONT,
            }}
          >
            reset
          </button>
        )}
      </div>
    </div>
  );
}

// Re-export so existing imports work
export { Btn };
